import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useQueryPublicCourse } from 'gql/queries/course';
import { useQueryLocalEditor } from 'gql/queries/local';
import {
  mutationEditorQuestionImportPreview,
  mutationEditorReset,
} from 'gql/mutations/editor';
import { useMutationQuestionImportPublicCourse } from 'gql/mutations/question';
import extract from 'helpers/extract';
import useModal from 'hooks/useModal';
import { trackEvent, reportError } from 'lib/tracking';
import { notify } from 'uikit/Notifications';

import QuestionImporter from './QuestionImporter';

const QuestionImporterContainer = ({ course, selectedCourse, ...props }) => {
  const { t } = useTranslation();
  const mutationQuestionImportPublicCourse =
    useMutationQuestionImportPublicCourse();
  const {
    data: {
      editor: { questionEditing },
    },
  } = useQueryLocalEditor();
  const { isModalOpen, handleModalOpen, handleModalClose } = useModal(false);
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        chapterId: yup
          .string()
          .trim()
          .matches(
            /^|([0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i,
          ),
        questionIds: yup
          .array()
          .of(yup.string().required().trim().uuid())
          .compact()
          .test(
            'length',
            t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportPublicCourses/QuestionImporter/questions-error-required',
            ),
            (questions) => {
              if (questionEditing) {
                return true;
              }
              return questions.length > 0;
            },
          ),
      }),
    [questionEditing, t],
  );
  const handleQuestionsImport = useCallback(
    async (values, form) => {
      try {
        form.setStatus(null);
        const variables = extract(values, validationSchema);
        await mutationQuestionImportPublicCourse({
          ...variables,
          questionIds: questionEditing
            ? [questionEditing.id]
            : variables.questionIds,
          courseId: course.id,
          selectedCourseId: selectedCourse.id,
        });
        if (!questionEditing) {
          mutationEditorReset();
        } else {
          mutationEditorQuestionImportPreview(null);
        }
        notify(
          'success',
          t(
            'Course/Teacher/Editor/QuestionImport/QuestionImportPublicCourses/QuestionImporter/questions-import-success',
          ),
        );
        trackEvent('questions', 'import-public-course');
      } catch (err) {
        form.setStatus('500');
        reportError('questions', 'import-public-course', err);
      }
    },
    [
      course,
      mutationQuestionImportPublicCourse,
      questionEditing,
      selectedCourse,
      validationSchema,
      t,
    ],
  );
  const handleClose = useCallback(() => {
    handleModalClose();
    mutationEditorQuestionImportPreview(null);
  }, [handleModalClose]);
  const { isLoading, data } = useQueryPublicCourse({
    variables: {
      courseId: selectedCourse.id,
    },
  });
  return (
    <QuestionImporter
      course={course}
      handleQuestionsImport={handleQuestionsImport}
      handleClose={handleClose}
      handleModalOpen={handleModalOpen}
      isLoading={isLoading}
      isModalOpen={isModalOpen}
      question={questionEditing}
      selectedCourse={data?.publicCourse || selectedCourse}
      validationSchema={validationSchema}
      t={t}
      {...props}
    />
  );
};

QuestionImporterContainer.propTypes = {
  course: PropTypes.object.isRequired,
  selectedCourse: PropTypes.object.isRequired,
};

export default QuestionImporterContainer;
